import React from 'react';
import PropTypes from 'prop-types';

import IconContext from '../context/IconContext';
import ProgressBar from "../ProgressBar"

// Define the type of the React component being given via context
type SvgComponentProps = React.SVGProps<SVGSVGElement> 
// Create an interface containing the icons needed for this component
interface IIconContext {
    IconClose: React.ComponentType<SvgComponentProps>;
    GIFSpinner: string;

    // To use custom SVGs, update the config to define these keys.
    IconActivityTrackerClose: React.ComponentType<SvgComponentProps>;
    GIFActivityTrackerSpinner: string;
}

interface ActivityTrackerProps {
    isHidden?: boolean,
    isCollapsed?: boolean,
    hideProgressBar?: boolean,
    message?: string,
    percentage: number,
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
    // Functions
    onCollapse: (collapseState: boolean) => void;
    onClose?: () => void;
}

/**
 * A small popup at the bottom to track the progress of an activity. Can be collapsed, 
 * 
 * ## _
 * ### Arguments
 * @param {boolean} isHidden - Togglable state for showing and hiding the activity tracker.
 * @param {boolean} isCollapsed - Toggleable state for collapsing the activity tracker.
 * @param {boolean} hideProgressBar - Flag to hide the progress bar. Usually just for debug or NFI states - if you are using this for something that doesn't need a progress bar, use another component.
 * @param {string} message - The displayed text on the activity tracker.
 * @param {number} percentage - The percentage of completion of the activity. Will be used in the progress bar.
 * @param {React.ReactNode} children - The contents inside the activity tracker. This is usually just text.
 * @param {string} className - CSS classes to apply to the activity tracker.
 * 
 * ## _
 * ### Functions
 * @param {function} onCollapse - Function that is called when the activity tracker is collapsed or uncollapsed. Passes a boolean that should be used to control `isCollapsed`.
 * @param {function} onClose - Function that is called when the activity tracker is closed.
 * 
 * ## _
 * ### Icons
 * Declare these in your application's icon configuration.
 * @inner `IconActivityTrackerClose` - An optional icon override for the close icon.
 */
class ActivityTracker extends React.Component<ActivityTrackerProps> {
    // Establish the context and tell TypeScript that we're TOTALLY having it in the shape of this thing.
    static contextType = IconContext;
    context!: IIconContext;

    static propTypes = {
        isHidden: PropTypes.bool,
        isCollapsed: PropTypes.bool,
        hideProgressBar: PropTypes.bool,
        message: PropTypes.string,
        percentage: PropTypes.number,
        children: PropTypes.node,
        className: PropTypes.string,
        onCollapse: PropTypes.func,
        onClose: PropTypes.func,
    }

    static defaultProps = {
        className: "",
    }

    handleUncollapse = () => {
        if (this.props.isCollapsed) {
            this.props.onCollapse(false);
        }
    }

    render() {
        const { isHidden, isCollapsed, hideProgressBar, message, percentage, children, className, onCollapse, onClose, ...rest } = this.props;
        const { 
            IconClose, GIFSpinner,
            IconActivityTrackerClose, GIFActivityTrackerSpinner, 
        } = this.context;
        
        const CloseIcon = IconActivityTrackerClose || IconClose
        const SpinnerGIF = GIFActivityTrackerSpinner || GIFSpinner

        let classes = `m-activity-tracker`
        if (!isHidden) classes += ` m-activity-tracker--shown`
        if (isCollapsed) classes += ` m-activity-tracker--collapsed`
        if (className) classes += ` ${className}`

        if (percentage >= 100 && onClose) onClose();

        return (
            <div className={classes} style={{zIndex: "2000"}} onClick={this.handleUncollapse}>
                <div className="m-activity-tracker__content-wrapper">
                    <img 
                        src={SpinnerGIF}
                        className="m-activity-tracker__icon"   
                    />
                    <p className="m-activity-tracker__message">{message}</p>
                    {!hideProgressBar &&
                        <ProgressBar
                            className='m-activity-tracker__progress-bar'
                            percentage={percentage} 
                            isLight
                            isSimple
                        />
                    }
                </div>
                <div className="m-activity-tracker__actions">
                    <button className='m-activity-tracker__close-button' onClick={() => onCollapse(true)}>
                        <CloseIcon className="m-activity-tracker__close-icon"/>
                    </button>
                </div>
            </div>
        );
    }
}

export default ActivityTracker;